// src/components/ErrorBoundary.jsx
// Single job: catch render errors in its subtree, report each one once to Sentry, and render
// the caller's fallback until reset (spec 2026-09-26-app-error-boundary-design.md).
// Reads: props (name, context, resetKey). Writes: one Sentry event per caught crash.
// Does NOT: catch event-handler/async errors, retry automatically, or render error text.
// Severity: captureException -- a crashed subtree is terminal until a user action (CC-IMPL-037).
// Do NOT also wire createRoot onCaughtError / Sentry.reactErrorHandler: every crash would be
// reported twice (spec §11).
import { Component } from "react";
import * as Sentry from "../lib/errorReporting";

const STACK_MAX = 2000;

export default class ErrorBoundary extends Component {
  state = { error: null, hasError: false };

  static getDerivedStateFromError(error) {
    return { error, hasError: true };
  }

  componentDidCatch(error, info) {
    const tags = { boundary: this.props.name };
    for (const [k, v] of Object.entries(this.props.context || {})) {
      if (v !== undefined && v !== null) tags[k] = String(v);
    }
    try {
      Sentry.captureException(error, {
        tags,
        extra: { componentStack: String(info?.componentStack || "").slice(0, STACK_MAX) },
      });
    } catch {
      // Reporting must never block the fallback.
    }
  }

  // prevState.hasError: never reset in the commit that caught the error. Navigating INTO a
  // crashing tab changes resetKey in that same commit; resetting there would re-render the
  // throwing child and report twice (Ultron P1-1).
  componentDidUpdate(prevProps, prevState) {
    if (this.state.hasError && prevState.hasError && !Object.is(prevProps.resetKey, this.props.resetKey)) {
      this.setState({ error: null, hasError: false });
    }
  }

  reset = () => this.setState({ error: null, hasError: false });

  render() {
    if (this.state.hasError) return this.props.fallback({ error: this.state.error, reset: this.reset });
    return this.props.children;
  }
}
